import React, { useEffect, useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search, Leaf, ArrowLeft } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { categoriesAPI, productsAPI } from '../lib/api';

import { FamilyCard } from '../components/catalogue/FamilyCard';
import { CatalogueFilters } from '../components/catalogue/CatalogueFilters';
import { WeeklyOffersSection } from '../components/catalogue/WeeklyOffersSection';
import { ProductsDisplay } from '../components/catalogue/ProductsDisplay';

const isInSeasonForMonth = (product, month) => {
    if (!product.season_start || !product.season_end || !month) return true;
    const m = month === 'now' ? (new Date().getMonth() + 1) : parseInt(month);
    const start = product.season_start;
    const end = product.season_end;
    if (start <= end) return m >= start && m <= end;
    return m >= start || m <= end;
};

export const Catalogue = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [categories, setCategories] = useState([]);
    const [productsByCategory, setProductsByCategory] = useState({});
    const [offersByCategory, setOffersByCategory] = useState([]);
    const [allProducts, setAllProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState(searchParams.get('search') || '');
    const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || '');
    const [selectedSeason, setSelectedSeason] = useState(searchParams.get('season') || '');
    const [expandedCategories, setExpandedCategories] = useState({});
    const [showWeeklyOffers, setShowWeeklyOffers] = useState(true);
    const [weeklyOffers, setWeeklyOffers] = useState([]);

    useEffect(() => {
        setSelectedCategory(searchParams.get('category') || '');
        setSearch(searchParams.get('search') || '');
        setSelectedSeason(searchParams.get('season') || '');
    }, [searchParams]);

    const sortedCategories = useMemo(
        () => [...categories].sort((a, b) => (a.order || 0) - (b.order || 0)),
        [categories]
    );

    useEffect(() => {
        const loadCategories = async () => {
            try {
                const res = await categoriesAPI.getAll();
                const list = res.data.categories || [];
                setCategories(list);
                const initial = {};
                list.forEach(cat => { initial[cat.id] = true; });
                setExpandedCategories(initial);
            } catch (error) {
                console.error('Error loading categories:', error);
            }
        };
        loadCategories();
    }, []);

    useEffect(() => {
        if (!selectedCategory && !search) {
            setAllProducts([]); setProductsByCategory({}); setOffersByCategory([]); setWeeklyOffers([]); setLoading(false);
            return;
        }
        const loadProducts = async () => {
            setLoading(true);
            try {
                const params = { limit: 100 };
                if (selectedCategory) params.category = selectedCategory;
                if (search) params.search = search;
                const [productsRes, offersRes] = await Promise.all([
                    productsAPI.getAll(params),
                    productsAPI.getWeeklyOffersByCategory()
                ]);
                let products = productsRes.data.products;
                if (selectedSeason) products = products.filter(p => isInSeasonForMonth(p, selectedSeason));
                setAllProducts(products);
                setOffersByCategory(offersRes.data.categories || []);
                setWeeklyOffers((offersRes.data.categories || []).flatMap(cat => cat.products || []));
                const grouped = {};
                products.forEach(p => { if (!grouped[p.category_id]) grouped[p.category_id] = []; grouped[p.category_id].push(p); });
                setProductsByCategory(grouped);
            } catch (error) {
                console.error('Error loading products:', error);
            } finally { setLoading(false); }
        };
        loadProducts();
    }, [selectedCategory, search, selectedSeason]);

    const handleFamilySelect = (categoryId) => setSearchParams({ category: categoryId });

    const handleCategoryChange = (value) => {
        setSelectedCategory(value === 'all' ? '' : value);
        const params = new URLSearchParams(searchParams);
        if (value === 'all') params.delete('category'); else params.set('category', value);
        setSearchParams(params);
    };

    const handleSeasonChange = (value) => {
        setSelectedSeason(value === 'all' ? '' : value);
        const params = new URLSearchParams(searchParams);
        if (value === 'all') params.delete('season'); else params.set('season', value);
        setSearchParams(params);
    };

    const handleSearch = (e) => {
        e.preventDefault();
        const params = new URLSearchParams(searchParams);
        if (search) params.set('search', search); else params.delete('search');
        setSearchParams(params);
    };

    const clearFilters = () => {
        setSearch(''); setSelectedCategory(''); setSelectedSeason('');
        setSearchParams({});
    };

    const toggleCategory = (categoryId) => {
        setExpandedCategories(prev => ({ ...prev, [categoryId]: !prev[categoryId] }));
    };

    const selectedCategoryData = categories.find(c => c.id === selectedCategory);
    const showFamilyGrid = !selectedCategory && !searchParams.get('search');
    const categoriesWithProducts = categories.filter(cat => productsByCategory[cat.id]?.length > 0);

    /* ─── FAMILY SELECTION VIEW ─── */
    if (showFamilyGrid) {
        return (
            <div className="min-h-screen bg-sand bg-botanical" data-testid="catalogue-page">
                <div className="bg-jungle/5 border-b border-jungle/10 relative overflow-hidden">
                    <div className="absolute right-0 top-0 w-64 h-full opacity-10">
                        <svg viewBox="0 0 200 300" fill="currentColor" className="text-jungle h-full w-full">
                            <path d="M100 30c-10 40-40 70-80 80 40 10 70 40 80 80 10-40 40-70 80-80-40-10-70-40-80-80z" />
                        </svg>
                    </div>
                    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
                        <div className="flex items-center gap-3 mb-2">
                            <div className="w-10 h-10 rounded-xl bg-leaf/10 flex items-center justify-center">
                                <Leaf className="w-5 h-5 text-leaf" />
                            </div>
                            <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight text-jungle">Notre Catalogue</h1>
                        </div>
                        <p className="mt-2 text-muted-foreground">Choisissez une famille pour découvrir nos produits</p>
                    </div>
                </div>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
                    <form onSubmit={handleSearch} className="flex gap-2 mb-10 max-w-xl">
                        <div className="relative flex-1">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input
                                placeholder="Rechercher un produit dans toutes les familles..."
                                value={search} onChange={(e) => setSearch(e.target.value)}
                                className="pl-10 bg-white/80 border-jungle/20 focus:border-leaf rounded-xl"
                                data-testid="search-input"
                            />
                        </div>
                        <Button type="submit" variant="secondary" className="bg-jungle/10 hover:bg-jungle/20 text-jungle rounded-xl" data-testid="search-button">
                            Rechercher
                        </Button>
                    </form>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="family-selection-grid">
                        {sortedCategories.map(cat => (
                            <FamilyCard key={cat.id} category={cat} onClick={() => handleFamilySelect(cat.id)} />
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    /* ─── PRODUCT LIST VIEW ─── */
    return (
        <div className="min-h-screen bg-sand bg-botanical" data-testid="catalogue-page">
            <div className="bg-jungle/5 border-b border-jungle/10 relative overflow-hidden">
                <div className="absolute right-0 top-0 w-64 h-full opacity-10">
                    <svg viewBox="0 0 200 300" fill="currentColor" className="text-jungle h-full w-full">
                        <path d="M100 30c-10 40-40 70-80 80 40 10 70 40 80 80 10-40 40-70 80-80-40-10-70-40-80-80z" />
                    </svg>
                </div>
                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
                    <button onClick={clearFilters} className="flex items-center gap-2 text-sm text-leaf hover:text-jungle mb-4 transition-colors" data-testid="back-to-families-btn">
                        <ArrowLeft className="w-4 h-4" />Retour aux familles
                    </button>
                    <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 rounded-xl bg-leaf/10 flex items-center justify-center">
                            <Leaf className="w-5 h-5 text-leaf" />
                        </div>
                        <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight text-jungle">
                            {selectedCategoryData ? selectedCategoryData.name : 'Résultats de recherche'}
                        </h1>
                    </div>
                    <p className="mt-2 text-muted-foreground">
                        {selectedCategoryData ? selectedCategoryData.description : `Recherche : "${search}"`}
                    </p>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <CatalogueFilters
                    search={search} onSearchChange={setSearch} onSearchSubmit={handleSearch}
                    selectedCategory={selectedCategory} selectedCategoryData={selectedCategoryData}
                    onCategoryChange={handleCategoryChange} selectedSeason={selectedSeason}
                    onSeasonChange={handleSeasonChange} categories={categories}
                    onClearFilters={clearFilters}
                />

                <WeeklyOffersSection
                    offersByCategory={offersByCategory} weeklyOffers={weeklyOffers}
                    selectedCategory={selectedCategory} showWeeklyOffers={showWeeklyOffers}
                    onToggleShow={() => setShowWeeklyOffers(v => !v)} search={search}
                />

                <ProductsDisplay
                    loading={loading} allProducts={allProducts} selectedCategory={selectedCategory}
                    categoriesWithProducts={categoriesWithProducts} productsByCategory={productsByCategory}
                    expandedCategories={expandedCategories} onToggleCategory={toggleCategory}
                    onClearFilters={clearFilters}
                />
            </div>
        </div>
    );
};
